import { t } from "@/lib/i18n/interpolate";
import {
  type DocBlock,
  type DocModel,
  type DocSection,
  ensurePeriod,
  FILL_LINE,
  firstSentence,
  linesOf,
  listItemsOf,
} from "@/lib/tool-kit/research-workflow/document";
import { wordsOf } from "@/lib/tool-kit/research-workflow/plain-language";

/**
 * An informed consent document built to the U.S. Common Rule.
 *
 * The structure follows 45 CFR 46.116 as revised in 2018: a concise key-information
 * section first (§46.116(a)(5)(i)), then every basic element of §46.116(b)(1)–(9) under
 * a plain-language question heading, the additional elements of §46.116(c) the
 * researcher switches on, and the documentation §46.117 asks for — a signature block, or
 * an agree-to-continue statement where the IRB has waived signatures.
 *
 * The key-information bullets quote the *first sentence* of the matching box, so the
 * summary and the detail can never disagree; the widget tells the researcher to put the
 * main point first, which is the rule the regulation is asking for anyway.
 *
 * Every word comes from `ConsentPhrases` — the widget's strings — so a translation of
 * the widget translates the document too.
 */

export type RiskLevel = "minimal" | "greater";
export type DataKind = "information" | "specimens";
export type FutureUse = "may" | "will-not" | "none";
export type Documentation = "signed" | "implied";
export type Recording = "none" | "audio" | "video" | "both";
export type ProfitShare = "omit" | "will" | "will-not";
export type GenomeSequencing = "omit" | "will" | "might";

export interface ConsentInput {
  title: string;
  researcher: string;
  /** Role and department, shown beside the name at the top of the form. */
  role: string;
  institution: string;
  advisor: string;
  contact: string;
  irbName: string;
  irbContact: string;
  protocol: string;
  /** Already formatted for the reader's locale, or empty. */
  versionDate: string;
  purpose: string;
  procedures: string;
  duration: string;
  experimental: string;
  /** Already formatted for the reader's locale, or empty. */
  participants: string;
  riskLevel: RiskLevel;
  risks: string;
  injury: string;
  benefits: string;
  alternatives: string;
  confidentiality: string;
  dataKind: DataKind;
  futureUse: FutureUse;
  payment: string;
  costs: string;
  withdrawal: string;
  termination: string;
  results: string;
  unforeseeable: boolean;
  pregnancy: boolean;
  newFindings: boolean;
  students: boolean;
  profit: ProfitShare;
  genome: GenomeSequencing;
  recording: Recording;
  documentation: Documentation;
  minimumAge: string;
  lar: boolean;
  witness: boolean;
  impliedAction: string;
}

export const EMPTY_CONSENT_INPUT: ConsentInput = {
  title: "",
  researcher: "",
  role: "",
  institution: "",
  advisor: "",
  contact: "",
  irbName: "",
  irbContact: "",
  protocol: "",
  versionDate: "",
  purpose: "",
  procedures: "",
  duration: "",
  experimental: "",
  participants: "",
  riskLevel: "minimal",
  risks: "",
  injury: "",
  benefits: "",
  alternatives: "",
  confidentiality: "",
  dataKind: "information",
  futureUse: "will-not",
  payment: "",
  costs: "",
  withdrawal: "",
  termination: "",
  results: "",
  unforeseeable: false,
  pregnancy: false,
  newFindings: false,
  students: false,
  profit: "omit",
  genome: "omit",
  recording: "none",
  documentation: "signed",
  minimumAge: "18",
  lar: false,
  witness: false,
  impliedAction: "",
};

/** The document's words. The widget's strings object satisfies this structurally. */
export interface ConsentPhrases {
  docTitle: string;
  metaStudy: string;
  metaResearcher: string;
  metaInstitution: string;
  metaAdvisor: string;
  metaProtocol: string;
  metaVersion: string;
  keyHeading: string;
  keyIntro: string;
  keyWhy: string;
  keyWhat: string;
  keyTime: string;
  keyRisks: string;
  keyBenefits: string;
  keyAlternatives: string;
  purposeHeading: string;
  purposeLead: string;
  proceduresHeading: string;
  durationSentence: string;
  experimentalLead: string;
  participantsSentence: string;
  risksHeading: string;
  unforeseeableSentence: string;
  pregnancySentence: string;
  benefitsHeading: string;
  alternativesHeading: string;
  alternativesDefault: string;
  confidentialityHeading: string;
  futureHeading: string;
  futureMay: string;
  futureWillNot: string;
  dataInformation: string;
  dataSpecimens: string;
  samplesHeading: string;
  profitWill: string;
  profitWillNot: string;
  genomeWill: string;
  genomeMight: string;
  paymentHeading: string;
  injuryHeading: string;
  stopHeading: string;
  voluntarySentence: string;
  studentsSentence: string;
  terminationLead: string;
  findingsHeading: string;
  newFindingsSentence: string;
  recordingHeading: string;
  recordingAudio: string;
  recordingVideo: string;
  recordingBoth: string;
  recordingChoice: string;
  contactHeading: string;
  contactStudy: string;
  contactAdvisor: string;
  contactInjury: string;
  contactRights: string;
  consentHeading: string;
  consentStatement: string;
  copySentence: string;
  fieldName: string;
  fieldSignature: string;
  fieldObtainerName: string;
  fieldObtainerSignature: string;
  fieldLarName: string;
  fieldLarRelationship: string;
  fieldLarSignature: string;
  fieldWitnessName: string;
  fieldWitnessSignature: string;
  impliedHeading: string;
  impliedStatement: string;
  impliedActionDefault: string;
  keepCopySentence: string;
  missingTitle: string;
  missingResearcher: string;
  missingInstitution: string;
  missingContact: string;
  missingIrb: string;
  missingIrbContact: string;
  missingPurpose: string;
  missingProcedures: string;
  missingDuration: string;
  missingRisks: string;
  missingBenefits: string;
  missingConfidentiality: string;
  missingInjury: string;
}

const SHORT_LINE = "____________";

function or(value: string, fallback: string): string {
  const clean = value.trim();
  return clean === "" ? fallback : clean;
}

function paragraphs(text: string, fallback: string): DocBlock[] {
  const lines = linesOf(text);
  if (lines.length === 0) {
    return [{ kind: "p", text: fallback }];
  }
  return lines.map((line) => ({ kind: "p", text: ensurePeriod(line) }));
}

function metaLines(input: ConsentInput, p: ConsentPhrases): string[] {
  const lines = [t(p.metaStudy, { value: or(input.title, p.missingTitle) })];
  const name = or(input.researcher, p.missingResearcher);
  const role = input.role.trim();
  lines.push(t(p.metaResearcher, { value: role ? `${name}, ${role}` : name }));
  if (input.institution.trim()) {
    lines.push(t(p.metaInstitution, { value: input.institution.trim() }));
  }
  if (input.advisor.trim()) {
    lines.push(t(p.metaAdvisor, { value: input.advisor.trim() }));
  }
  if (input.protocol.trim()) {
    lines.push(t(p.metaProtocol, { value: input.protocol.trim() }));
  }
  if (input.versionDate) {
    lines.push(t(p.metaVersion, { value: input.versionDate }));
  }
  return lines;
}

function summary(text: string, fallback: string, locale: string): string {
  return text.trim() ? firstSentence(text, locale) : fallback;
}

function keySection(
  input: ConsentInput,
  p: ConsentPhrases,
  locale: string
): DocSection {
  const items = [
    t(p.keyWhy, {
      text: summary(input.purpose, p.missingPurpose, locale),
    }),
    t(p.keyWhat, {
      text: summary(input.procedures, p.missingProcedures, locale),
    }),
    t(p.keyTime, {
      text: ensurePeriod(or(input.duration, p.missingDuration)),
    }),
    t(p.keyRisks, { text: summary(input.risks, p.missingRisks, locale) }),
    t(p.keyBenefits, {
      text: summary(input.benefits, p.missingBenefits, locale),
    }),
    t(p.keyAlternatives, {
      text: summary(input.alternatives, p.alternativesDefault, locale),
    }),
  ];
  return {
    heading: p.keyHeading,
    blocks: [
      { kind: "p", text: p.keyIntro },
      { kind: "ul", items },
    ],
  };
}

function purposeSection(input: ConsentInput, p: ConsentPhrases): DocSection {
  const [first, ...rest] = paragraphs(input.purpose, p.missingPurpose);
  const opening =
    first?.kind === "p" ? `${p.purposeLead} ${first.text}` : p.purposeLead;
  return {
    heading: p.purposeHeading,
    blocks: [{ kind: "p", text: opening }, ...rest],
  };
}

function proceduresSection(input: ConsentInput, p: ConsentPhrases) {
  const blocks = paragraphs(input.procedures, p.missingProcedures);
  blocks.push({
    kind: "p",
    text: t(p.durationSentence, {
      duration: or(input.duration, p.missingDuration),
    }),
  });
  const experimental = listItemsOf(input.experimental);
  if (experimental.length > 0) {
    blocks.push({ kind: "p", text: p.experimentalLead });
    blocks.push({ kind: "ul", items: experimental.map(ensurePeriod) });
  }
  if (input.participants) {
    blocks.push({
      kind: "p",
      text: t(p.participantsSentence, { count: input.participants }),
    });
  }
  return { heading: p.proceduresHeading, blocks };
}

function risksSection(input: ConsentInput, p: ConsentPhrases): DocSection {
  const blocks = paragraphs(input.risks, p.missingRisks);
  if (input.unforeseeable) {
    blocks.push({ kind: "p", text: p.unforeseeableSentence });
  }
  if (input.pregnancy) {
    blocks.push({ kind: "p", text: p.pregnancySentence });
  }
  return { heading: p.risksHeading, blocks };
}

function dataWord(input: ConsentInput, p: ConsentPhrases): string {
  return input.dataKind === "specimens" ? p.dataSpecimens : p.dataInformation;
}

function futureSection(
  input: ConsentInput,
  p: ConsentPhrases
): DocSection | null {
  if (input.futureUse === "none") {
    return null;
  }
  const template = input.futureUse === "may" ? p.futureMay : p.futureWillNot;
  return {
    heading: p.futureHeading,
    blocks: [{ kind: "p", text: t(template, { data: dataWord(input, p) }) }],
  };
}

function samplesSection(
  input: ConsentInput,
  p: ConsentPhrases
): DocSection | null {
  if (input.dataKind !== "specimens") {
    return null;
  }
  const blocks: DocBlock[] = [];
  if (input.profit === "will") {
    blocks.push({ kind: "p", text: p.profitWill });
  } else if (input.profit === "will-not") {
    blocks.push({ kind: "p", text: p.profitWillNot });
  }
  if (input.genome === "will") {
    blocks.push({ kind: "p", text: p.genomeWill });
  } else if (input.genome === "might") {
    blocks.push({ kind: "p", text: p.genomeMight });
  }
  return blocks.length > 0 ? { heading: p.samplesHeading, blocks } : null;
}

function paymentSection(input: ConsentInput, p: ConsentPhrases) {
  const blocks = [
    ...paragraphs(input.payment, ""),
    ...paragraphs(input.costs, ""),
  ].filter((block) => block.kind !== "p" || block.text !== "");
  return blocks.length > 0 ? { heading: p.paymentHeading, blocks } : null;
}

function injurySection(
  input: ConsentInput,
  p: ConsentPhrases
): DocSection | null {
  if (input.riskLevel === "minimal") {
    return null;
  }
  return {
    heading: p.injuryHeading,
    blocks: paragraphs(input.injury, p.missingInjury),
  };
}

function stopSection(input: ConsentInput, p: ConsentPhrases): DocSection {
  const blocks: DocBlock[] = [{ kind: "p", text: p.voluntarySentence }];
  if (input.students) {
    blocks.push({
      kind: "p",
      text: t(p.studentsSentence, {
        institution: or(input.institution, p.missingInstitution),
      }),
    });
  }
  blocks.push(...paragraphs(input.withdrawal, "").filter(hasText));
  if (input.termination.trim()) {
    blocks.push({
      kind: "p",
      text: `${p.terminationLead} ${ensurePeriod(input.termination.trim())}`,
    });
  }
  return { heading: p.stopHeading, blocks };
}

function hasText(block: DocBlock): boolean {
  return block.kind !== "p" || block.text !== "";
}

function findingsSection(
  input: ConsentInput,
  p: ConsentPhrases
): DocSection | null {
  const blocks: DocBlock[] = [];
  if (input.newFindings) {
    blocks.push({ kind: "p", text: p.newFindingsSentence });
  }
  blocks.push(...paragraphs(input.results, "").filter(hasText));
  return blocks.length > 0 ? { heading: p.findingsHeading, blocks } : null;
}

const RECORDING_KEYS = {
  audio: "recordingAudio",
  video: "recordingVideo",
  both: "recordingBoth",
} as const;

function recordingSection(
  input: ConsentInput,
  p: ConsentPhrases
): DocSection | null {
  if (input.recording === "none") {
    return null;
  }
  return {
    heading: p.recordingHeading,
    blocks: [
      { kind: "p", text: p[RECORDING_KEYS[input.recording]] },
      {
        kind: "fields",
        items: [t(p.recordingChoice, { short: SHORT_LINE })],
      },
    ],
  };
}

function contactSection(input: ConsentInput, p: ConsentPhrases): DocSection {
  const researcher = or(input.researcher, p.missingResearcher);
  const contact = or(input.contact, p.missingContact);
  const blocks: DocBlock[] = [
    { kind: "p", text: t(p.contactStudy, { researcher, contact }) },
  ];
  if (input.advisor.trim()) {
    blocks.push({
      kind: "p",
      text: t(p.contactAdvisor, { advisor: input.advisor.trim() }),
    });
  }
  blocks.push({ kind: "p", text: t(p.contactInjury, { researcher, contact }) });
  blocks.push({
    kind: "p",
    text: t(p.contactRights, {
      irb: or(input.irbName, p.missingIrb),
      irbContact: or(input.irbContact, p.missingIrbContact),
    }),
  });
  return { heading: p.contactHeading, blocks };
}

function signedSection(input: ConsentInput, p: ConsentPhrases): DocSection {
  const line = { line: FILL_LINE, short: SHORT_LINE };
  const fields = [
    t(p.fieldName, line),
    t(p.fieldSignature, line),
    t(p.fieldObtainerName, line),
    t(p.fieldObtainerSignature, line),
  ];
  if (input.lar) {
    fields.push(
      t(p.fieldLarName, line),
      t(p.fieldLarRelationship, line),
      t(p.fieldLarSignature, line)
    );
  }
  if (input.witness) {
    fields.push(t(p.fieldWitnessName, line), t(p.fieldWitnessSignature, line));
  }
  return {
    heading: p.consentHeading,
    blocks: [
      {
        kind: "p",
        text: t(p.consentStatement, { age: or(input.minimumAge, "18") }),
      },
      { kind: "fields", items: fields },
      { kind: "p", text: p.copySentence },
    ],
  };
}

function impliedSection(input: ConsentInput, p: ConsentPhrases): DocSection {
  return {
    heading: p.impliedHeading,
    blocks: [
      {
        kind: "p",
        text: t(p.impliedStatement, {
          action: or(input.impliedAction, p.impliedActionDefault),
          age: or(input.minimumAge, "18"),
        }),
      },
      { kind: "p", text: p.keepCopySentence },
    ],
  };
}

/** The whole consent document, in the order the sections are read. */
export function buildConsentForm(
  input: ConsentInput,
  p: ConsentPhrases,
  locale = "en"
): DocModel {
  const sections: (DocSection | null)[] = [
    keySection(input, p, locale),
    purposeSection(input, p),
    proceduresSection(input, p),
    risksSection(input, p),
    {
      heading: p.benefitsHeading,
      blocks: paragraphs(input.benefits, p.missingBenefits),
    },
    {
      heading: p.alternativesHeading,
      blocks: paragraphs(input.alternatives, p.alternativesDefault),
    },
    {
      heading: p.confidentialityHeading,
      blocks: paragraphs(input.confidentiality, p.missingConfidentiality),
    },
    futureSection(input, p),
    samplesSection(input, p),
    paymentSection(input, p),
    injurySection(input, p),
    stopSection(input, p),
    findingsSection(input, p),
    recordingSection(input, p),
    contactSection(input, p),
    input.documentation === "signed"
      ? signedSection(input, p)
      : impliedSection(input, p),
  ];
  return {
    title: p.docTitle,
    meta: metaLines(input, p),
    sections: sections.filter((s): s is DocSection => s !== null),
  };
}

/* ------------------------------------------------------------------ checklist */

export type ElementStatus = "covered" | "missing" | "standard" | "na";

export type ConsentElement =
  | "keyInfo"
  | "b1"
  | "b2"
  | "b3"
  | "b4"
  | "b5"
  | "b6"
  | "b7"
  | "b8"
  | "b9"
  | "documentation";

export interface ElementCheck {
  element: ConsentElement;
  status: ElementStatus;
}

function filled(...values: string[]): boolean {
  return values.every((value) => value.trim() !== "");
}

function status(ok: boolean): ElementStatus {
  return ok ? "covered" : "missing";
}

/**
 * Which required element each part of the form satisfies, and which still needs the
 * researcher's words. "standard" marks an element covered by the generator's own
 * wording (voluntary participation, a default alternative), "na" one that does not apply
 * to this study (injury provisions in minimal-risk research, future-use statements when
 * nothing identifiable is collected).
 */
export function checkConsentElements(input: ConsentInput): ElementCheck[] {
  const injury: ElementStatus =
    input.riskLevel === "minimal" ? "na" : status(filled(input.injury));
  return [
    {
      element: "keyInfo",
      status: status(
        filled(
          input.purpose,
          input.procedures,
          input.duration,
          input.risks,
          input.benefits
        )
      ),
    },
    {
      element: "b1",
      status: status(filled(input.purpose, input.procedures, input.duration)),
    },
    { element: "b2", status: status(filled(input.risks)) },
    { element: "b3", status: status(filled(input.benefits)) },
    {
      element: "b4",
      status: filled(input.alternatives) ? "covered" : "standard",
    },
    { element: "b5", status: status(filled(input.confidentiality)) },
    { element: "b6", status: injury },
    {
      element: "b7",
      status: status(
        filled(input.researcher, input.contact, input.irbName, input.irbContact)
      ),
    },
    { element: "b8", status: "standard" },
    {
      element: "b9",
      status: input.futureUse === "none" ? "na" : "covered",
    },
    { element: "documentation", status: "covered" },
  ];
}

const ANONYMOUS_WORDS = new Set(["anonymous", "anonymously", "anonymity"]);

/** True when the researcher promised anonymity somewhere — worth a second look. */
export function mentionsAnonymity(input: ConsentInput): boolean {
  const text = [
    input.purpose,
    input.procedures,
    input.risks,
    input.confidentiality,
    input.withdrawal,
  ].join(" ");
  return wordsOf(text).some((word) => ANONYMOUS_WORDS.has(word));
}

/** Everything the researcher typed as prose — what the plain-language check reads. */
export function consentProse(input: ConsentInput): string {
  return [
    input.purpose,
    input.procedures,
    input.experimental,
    input.risks,
    input.injury,
    input.benefits,
    input.alternatives,
    input.confidentiality,
    input.payment,
    input.costs,
    input.withdrawal,
    input.termination,
    input.results,
  ]
    .map((text) => text.trim())
    .filter((text) => text.length > 0)
    .join("\n");
}
